// PersonPicker — a labeled select of users (Epic E9 P1-E9-1/E9-3, AC-ACT-2.1).
//
// Options are the shared `/users` roster (via usePeople); the option VALUE is always a real
// userId while the visible option text is the person's display name (plus title when known),
// so forms post governed user ids without ever asking humans to type `USR-…` tokens.
//
// Import by direct path (not via components/index.js):
//   import { PersonPicker } from '../../components/primitives/PersonPicker.jsx';

import { useMemo } from 'react';
import { FormField, Select } from '../primitives.jsx';
import { usePeople } from '../../lib/usePeople.js';

export function PersonPicker({
  id,
  name,
  label,
  value,
  onChange,
  placeholder = 'Select a person…',
  required,
  hint,
  error,
  disabled,
}) {
  const { people, nameOf } = usePeople();

  const options = useMemo(() => {
    const opts = people.map((person) => ({
      value: person.id,
      label: person.title
        ? `${person.displayName || person.id} — ${person.title}`
        : person.displayName || person.id,
    }));
    // Keep a pre-set value selectable even when it is not in the loaded roster
    // (e.g. roster still loading, or a legacy/unknown id already on the record).
    if (value && !people.some((person) => person.id === value)) {
      opts.unshift({ value, label: nameOf(value) });
    }
    return opts;
  }, [people, value, nameOf]);

  const control = (
    <Select
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
    />
  );

  if (!label) return control;

  return (
    <FormField label={label} htmlFor={id} required={required} hint={hint} error={error}>
      {control}
    </FormField>
  );
}
